<template>
  <BasePageContainer
    class="pt-10 pb-11 mb-11">
    <div class="d-flex justify-content-between align-items-start">
      <MonthPickerWithArrow
        :value="selectedYearMonthDate"
        format="yyyy年M月 月間目標"
        @input="onInputSelectedYearMonthDate" />
      <div class="d-flex flex-column align-items-end mt-1">
        <SvgTextButton
          class="mb-4"
          active
          bold
          :src="require('@/assets/images/icon_chart.svg')"
          @click="backToChart">
          チャートに戻る
        </SvgTextButton>
        <MiniButton
          v-if="isManager"
          to="/admin/mandala_evaluations_export">
          チャート一括出力
        </MiniButton>
      </div>
    </div>
    <MandalaEvaluationForm
      v-if="!isLoading"
      ref="form"
      :key="mandalaEvaluationFormKey"
      :response-errors="responseErrors"
      :selected-year-month="selectedYearMonth"
      :mandala-evaluation="mandalaEvaluation"
      :last-month-mandala-evaluation="lastMonthMandalaEvaluation"
      :latest-mandala-evaluation="latestMandalaEvaluation"
      :mandala-goal-templates="mandalaGoalTemplates"
      :is-mine="isMyEvaluation"
      @submit="submit">
      <template
        v-if="mandalaEvaluation"
        #like-button>
        <div
          class="d-flex align-items-center">
          <LikeButton
            :active="isLike"
            :disabled="isParentMember"
            @click="onClickLikeButton" />
          <CountButton
            :active="isLike"
            :count="likeUsers.length"
            @click="showLikeUsersModal = true" />
        </div>
      </template>
    </MandalaEvaluationForm>
    <MandalaEvaluationForm
      v-else
      key="loading-form"
      :selected-year-month="selectedYearMonth" />
    <BaseButton
      v-if="canRemand"
      class="mt-4"
      @click="remandMandalaEvaluation">
      提出差し戻し
    </BaseButton>
    <template v-if="mandalaEvaluation && canUseComment">
      <hr color="darkgrey">
      <LabelText>コメント</LabelText>
      <CommentSectionWrapper :key="mandalaEvaluation.id">
        <CommentItem
          v-for="comment in comments"
          :ref="comment.id"
          :key="comment.id"
          :comment="comment"
          @delete="deleteComment" />
        <CommentInput
          v-if="!isParentMember"
          :id="mandalaEvaluation.id"
          ref="commentForm"
          field-id="mandala_goal_self_evaluation"
          @submit="addComment" />
      </CommentSectionWrapper>
    </template>
    <div class="d-flex justify-content-end py-4">
      <div class="d-flex flex-column align-items-end">
        <IconTextButton
          class="font-weight-bold mb-2"
          active
          :icon-size="30"
          icon="arrow_drop_up"
          @click="scrollToTop">
          最上部へ
        </IconTextButton>
        <SvgTextButton
          active
          bold
          :src="require('@/assets/images/icon_chart.svg')"
          @click="backToChart">
          チャートに戻る
        </SvgTextButton>
      </div>
    </div>
    <div
      v-if="isManager"
      class="d-flex justify-content-between align-items-center">
      <MiniButton
        to="/admin/mandala_goal_templates"
        variant="dark">
        テンプレート管理
      </MiniButton>
      <MiniButton
        to="/admin/mandala_evaluations_export">
        チャート一括出力
      </MiniButton>
    </div>

    <LikeUsersModal
      v-model="showLikeUsersModal"
      :users="likeUsers" />

    <FixedBottomLeftArea v-if="isPrevRouteMandalaEvaluationsExportPreview">
      <BaseButton
        color="l-blue"
        style="border-radius: 32px;"
        @click="$router.push({
          name: 'mandalaEvaluationsExportPreview',
          query: {
            ...prevRoute.query,
            prevRouteMemberId: selectedMemberId
          }
        })">
        出力に戻る
      </BaseButton>
    </FixedBottomLeftArea>
  </BasePageContainer>
</template>

<script>
import BaseButton from '@/components/parts/BaseButton.vue'
import MiniButton from '@/components/parts/MiniButton.vue'
import SvgTextButton from '@/components/parts/SvgTextButton.vue'
import { mapGetters, mapState } from 'vuex'
import MandalaEvaluationForm from '@/components/view/mandala_evaluation/MandalaEvaluationForm.vue'
import MonthPickerWithArrow from '@/components/parts/MonthPickerWithArrow.vue'
import CommentSectionWrapper from '@/components/view/CommentSectionWrapper.vue'
import CommentInput from '@/components/view/CommentInput.vue'
import CommentItem from '@/components/view/CommentItem.vue'
import LabelText from '@/components/parts/LabelText.vue'
import LikeButton from '@/components/parts/LikeButton.vue'
import CountButton from '@/components/parts/CountButton.vue'
import LikeUsersModal from '@/components/view/LikeUsersModal.vue'
import IconTextButton from '@/components/parts/IconTextButton.vue'
import FixedBottomLeftArea from '@/components/parts/FixedBottomLeftArea.vue'
import { addMonths, format, startOfMonth } from 'date-fns'
import confirmIfFormChanged from '@/components/mixin/confirmIfFormChanged'

export default {
  components: {
    MiniButton,
    SvgTextButton,
    MandalaEvaluationForm,
    MonthPickerWithArrow,
    BaseButton,
    LabelText,
    CommentSectionWrapper,
    CommentItem,
    CommentInput,
    LikeButton,
    CountButton,
    LikeUsersModal,
    IconTextButton,
    FixedBottomLeftArea
  },
  // 画面遷移時に確認ダイアログを表示するためにmixinを使用
  mixins: [confirmIfFormChanged(), confirmIfFormChanged('commentForm')],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },
  data() {
    return {
      mandalaEvaluation: null,
      lastMonthMandalaEvaluation: null,
      latestMandalaEvaluation: null,
      mandalaGoalTemplates: [],
      responseErrors: {},
      comments: [],
      commentResponseErrors: {},
      likeUsers: [],
      showLikeUsersModal: false,
      isLoading: false,
      prevRoute: null
    }
  },
  computed: {
    ...mapGetters('auth', ['isManager', 'isParentMember']),
    ...mapState('auth', ['user']),
    mandalaEvaluationFormKey() {
      if (!this.mandalaEvaluation) {
        return null
      }
      return `${this.mandalaEvaluation.id}_${this.mandalaEvaluation.updatedAt}`
    },
    selectedMemberId() {
      return this.$route.params.id
    },
    hasMandalaEvaluation() {
      return this.mandalaEvaluation !== null
    },
    isStatusSubmitted() {
      return this.hasMandalaEvaluation && this.mandalaEvaluation.status === 'submitted'
    },
    canRemand() {
      return this.isManager && this.isStatusSubmitted
    },
    selectedYearMonthDate() {
      const yearMonthQuery = this.$route.query.yearMonth
      if (!yearMonthQuery) {
        return startOfMonth(new Date())
      }
      return startOfMonth(new Date(yearMonthQuery))
    },
    selectedYearMonth() {
      return format(this.selectedYearMonthDate, 'yyyy-MM-dd')
    },
    lastYearMonth() {
      return format(startOfMonth(addMonths(this.selectedYearMonthDate, -1)), 'yyyy-MM-dd')
    },
    isMyEvaluation() {
      return this.$route.params.id === this.user.id
    },
    selectedMandalaGoalTemplate() {
      if (this.mandalaEvaluation === null) {
        return null
      }
      return this.mandalaGoalTemplates.find(template => template.id === this.mandalaEvaluation.mandalaGoalTemplate)
    },
    /**
     * @description
     * コメントを閲覧、作成できるのは
     * - 管理者
     * - 本人
     * - テンプレートでコメントがロックされていない場合
     */
    canUseComment() {
      if (!this.selectedMandalaGoalTemplate) {
        return false
      }
      return this.isManager || this.isMyEvaluation || !this.selectedMandalaGoalTemplate.isLockComment
    },
    /**
     * @description
     * いいね機能を使えるのは
     * - 管理者
     * - 本人
     * - テンプレートで今月のまとめがロックされていない場合
     */
    canUseLike() {
      if (!this.selectedMandalaGoalTemplate) {
        return false
      }
      return this.isManager || this.isMyEvaluation || !this.selectedMandalaGoalTemplate.isLockSummary
    },
    isLike() {
      return this.likeUsers.map(user => user.id).includes(this.user.id)
    },
    scrollTo() {
      return this.$route.query.scrollTo
    },
    isPrevRouteMandalaEvaluationsExportPreview() {
      if (!this.prevRoute) {
        return false
      }
      return this.prevRoute.name === 'mandalaEvaluationsExportPreview'
    },
  },
  watch: {
    async selectedYearMonth() {
      this.$store.commit('startLoading')
      this.isLoading = true
      this.resetData()
      await Promise.all([
        this.fetchMandalaGoalTemplates(),
        this.fetchMandalaEvaluation(),
        this.fetchLatestMandalaEvaluation()
      ])
      await this.fetchRelatedDataByMandalaEvaluation()
      this.isLoading = false
      this.$store.commit('finishLoading')
    }
  },
  async created() {
    this.$store.commit('startLoading')
    this.isLoading = true
    await Promise.all([
      this.fetchMandalaGoalTemplates(),
      this.fetchMandalaEvaluation(),
      this.fetchLatestMandalaEvaluation()
    ])
    await this.fetchRelatedDataByMandalaEvaluation()
    this.isLoading = false
    if (this.scrollTo) {
      this.scrollToRef(this.scrollTo)
    }
    this.$store.commit('finishLoading')
  },
  methods: {
    async submit({ status, formData }) {
      this.responseErrors = {}
      if (status === 'published') {
        if (!window.confirm('提出後に内容を変更する場合は、指導者に「差し戻し」を依頼してください。提出しますか？')) return
      }
      this.$store.commit('startLoading')
      if (!this.hasMandalaEvaluation) {
        await this.createMandalaEvaluation(formData)
      } else {
        await this.updateMandalaEvaluation(formData)
      }
      this.$store.commit('finishLoading')
    },
    resetData() {
      this.comments = []
      this.likeUsers = []
      this.responseErrors = {}
    },
    /**
     * @description 自己評価に紐づくコメントといいねしたユーザを取得するための関数
     */
    fetchRelatedDataByMandalaEvaluation() {
      if (!this.mandalaEvaluation) {
        return []
      }
      let promises = []
      if (this.canUseComment) {
        promises.push(this.fetchComments(this.mandalaEvaluation.id))
      }
      if (this.canUseLike) {
        promises.push(this.fetchLikeUsers(this.mandalaEvaluation.id))
      }
      return Promise.all(promises)
    },
    fetchMandalaEvaluation() {
      return this.$axios.get('/mandala_goal_self_evaluations/', {
        params: {
          year_month__in: [this.selectedYearMonth, this.lastYearMonth].join(','),
          user__in: this.selectedMemberId,
          order_by: '-year_month',
          limit: 2
        }
      }).then(res => {
        const evaluations = res.data.results
        const currentMandalaEvaluation = evaluations.find(evaluation => evaluation.yearMonth === this.selectedYearMonth) || null
        const lastMonthMandalaEvaluation = evaluations.find(evaluation => evaluation.yearMonth === this.lastYearMonth) || null
        this.mandalaEvaluation = currentMandalaEvaluation
        this.lastMonthMandalaEvaluation = lastMonthMandalaEvaluation
      })
    },
    fetchLatestMandalaEvaluation() {
      return this.$axios.get('/mandala_goal_self_evaluations/', {
        params: {
          order_by: '-year_month',
          year_month__lte: this.selectedYearMonth,
          user__in: this.selectedMemberId,
          limit: 1
        }
      }).then(res => {
        const newEvaluation = res.data.results[0]
        this.latestMandalaEvaluation = newEvaluation || null
      })
    },
    /**
     * @description 行動目標のスコアに関するエラーがないかを確認するメソッド
     */
    hasActionGoalsScoreError(responseErrors) {
      if (!('mandalaGoalSelfEvaluationEntries' in responseErrors)) {
        return false
      }
      return responseErrors.mandalaGoalSelfEvaluationEntries.some((entry) => {
        if (!('mandalaGoalSelfEvaluationActionGoals' in entry)) {
          return false
        }
        return entry.mandalaGoalSelfEvaluationActionGoals.some(actionGoal => 'score' in actionGoal)
      })
    },
    /**
     * @description 作成時・更新時のエラーの扱いを共通化させるためのメソッド
     */
    handleCommonErrors(responseErrors) {
      const { nonFieldErrors } = responseErrors
      if (nonFieldErrors) {
        this.$store.commit('notify/set', {type: 'danger', message: nonFieldErrors[0]})
        return
      }
      this.responseErrors = responseErrors
      if (this.hasActionGoalsScoreError(responseErrors)) {
        this.$store.commit('notify/set', {type: 'danger', message: '自己評価が終わっていない行動目標があります。確認してください。'})
        return
      }
      this.$store.commit('notify/set', {type: 'danger', message: '入力内容に不備があります。ご確認の上再度実行してください。'})
    },
    createMandalaEvaluation(formData) {
      return this.$axios.post('/mandala_goal_self_evaluations/', formData).then(res => {
        const newMandalaEvaluation = res.data
        this.mandalaEvaluation = newMandalaEvaluation
        const { status, yearMonth } = newMandalaEvaluation
        const isSubmitted = status === 'submitted'
        const yearMonthDate = new Date(yearMonth)
        this.$store.commit('notify/set', { message: `${format(yearMonthDate, 'yyyy年MM月')}の自己評価を${ isSubmitted ? '提出' : '作成' }しました`})

        this.$refs.form.setCanEdit(false)
        this.$refs.form.$refs.observer.reset()
        this.canLeave = true
        this.scrollToTop()
      }).catch(error => {
        const responseErrors = error.response.data
        this.handleCommonErrors(responseErrors)
      })
    },
    updateMandalaEvaluation(formData) {
      return this.$axios.patch(`/mandala_goal_self_evaluations/${this.mandalaEvaluation.id}/`, formData).then((res) => {
        const newMandalaEvaluation = res.data
        this.mandalaEvaluation = newMandalaEvaluation
        const { status, yearMonth } = newMandalaEvaluation
        const isSubmitted = status === 'submitted'
        const yearMonthDate = new Date(yearMonth)
        this.$store.commit('notify/set', { message: `${format(yearMonthDate, 'yyyy年MM月')}の自己評価を${ isSubmitted ? '提出' : '更新' }しました`})

        this.$refs.form.setCanEdit(false)
        this.$refs.form.$refs.observer.reset()
        this.canLeave = true
        this.scrollToTop()
      }).catch(error => {
        const responseErrors = error.response.data
        this.handleCommonErrors(responseErrors)
      })
    },
    async remandMandalaEvaluation() {
      if (!window.confirm('差し戻しを行うと提出者が編集可能になります。差し戻しますか？ ')) return
      this.$store.commit('startLoading')
      this.$axios.post(`/mandala_goal_self_evaluations/${this.mandalaEvaluation.id}/remand/`).then(() => {
        this.fetchMandalaEvaluation()
        this.$store.commit('notify/set', { message: 'この自己評価の提出を差し戻しました。'})
      }).catch(error => {
        console.error(error)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    fetchMandalaGoalTemplates() {
      return this.$axios.get('/mandala_goal_templates/', {
        params: {
          orderBy: 'display_order'
        }
      }).then(res => {
        this.mandalaGoalTemplates = res.data.results
      })
    },
    fetchComments(mandalaEvaluationId) {
      this.$axios.get(`/mandala_goal_self_evaluations/${mandalaEvaluationId}/comments/`).then(res => {
        this.comments = res.data.results
      })
    },
    fetchLikeUsers(mandalaEvaluationId) {
      this.$axios.get(`/mandala_goal_self_evaluations/${mandalaEvaluationId}/like_users/`).then((res) => {
        this.likeUsers = res.data.results
      })
    },
    onClickLikeButton() {
      if (!this.mandalaEvaluation) {
        return
      }
      this.isLike ? this.postDisLike(this.mandalaEvaluation.id) : this.postLike(this.mandalaEvaluation.id)
    },
    postLike(mandalaEvaluationId) {
      this.$axios.post(`/mandala_goal_self_evaluations/${mandalaEvaluationId}/like/`).then(() => {
        this.fetchLikeUsers(mandalaEvaluationId)
      })
    },
    postDisLike(mandalaEvaluationId) {
      this.$axios.post(`/mandala_goal_self_evaluations/${mandalaEvaluationId}/dislike/`).then(() => {
        this.fetchLikeUsers(mandalaEvaluationId)
      })
    },
    async addComment(comment) {
      this.$store.commit('startLoading')
      await this.$axios.post('/mandala_goal_self_evaluation_comments/', comment).then(() => {
        this.fetchComments(this.mandalaEvaluation.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async deleteComment(commentId) {
      if (!window.confirm('コメントを削除してもよろしいですか?')) return
      this.$store.commit('startLoading')
      await this.$axios.delete(`/mandala_goal_self_evaluation_comments/${commentId}/`).then(() => {
        this.$store.commit('notify/set', {message: 'コメントを削除しました。'})
        this.fetchComments(this.mandalaEvaluation.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    onInputSelectedYearMonthDate(date) {
      const yearMonthStr = format(date, 'yyyy-MM')
      this.$router.push({ path: `/members/${this.selectedMemberId}/mandala_evaluation`, query: { yearMonth: yearMonthStr }})
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0)
      })
    },
    scrollToEl(el) {
      const rect = el.getBoundingClientRect()
      const elementTop = rect.top + window.pageYOffset
      const offset = 110
      // setTimeoutを使わないとDatepickerで選択時に移動しない
      setTimeout(() => {
        window.scrollTo(0, elementTop - offset)
      }, 1)
    },
    scrollToRef(refId) {
      this.$nextTick(() => {
        window.setTimeout(() => {
          const refAndFormRef = { ...this.$refs, ...this.$refs.form.$refs }
          const ref = Array.isArray(refAndFormRef[refId]) ? refAndFormRef[refId][0] : refAndFormRef[refId]
          if (ref) {
            this.scrollToEl(ref.$el)
          }
        }, 300)
      })
    },
    backToChart() {
      this.$router.push({
        path: `/members/${this.selectedMemberId}`,
        query: {
          mandalaSectionInitialDate: this.selectedYearMonth
        }
      })
    }
  }
}
</script>

<style>

</style>